import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import './Savings.css';

const TransferModal = ({ onClose, onTransfer, fromStaff, currentBalance }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [queryText, setQueryText] = useState('');

  const fetchStaffList = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', '!=', 'admin'));
      const querySnapshot = await getDocs(q);
      
      const staff = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(user => user.id !== fromStaff.id); // Exclude the sender
      
      setStaffList(staff);
      setError('');
    } catch (err) {
      console.error('Error fetching staff list:', err);
      setError('Failed to load staff list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedStaff) {
      setError('Please select a staff member to transfer to');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > currentBalance) {
      setError('Transfer amount exceeds available balance');
      return;
    }

    onTransfer(selectedStaff, amount, description);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Transfer</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="transfer-info">
            <p>From: <strong>{fromStaff.displayName || fromStaff.email}</strong></p>
            <p>Available Balance: <strong>{formatAmount(currentBalance)}</strong></p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Transfer To</label>
              <input
                type="text"
                placeholder="Search staff by name or email"
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                style={{ marginBottom: '8px' }}
              />
              <div className="staff-select">
                {loading ? (
                  <p>Loading staff list...</p>
                ) : (
                  staffList
                    .filter(s => {
                      const q = queryText.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (s.displayName || '').toLowerCase().includes(q) ||
                        (s.email || '').toLowerCase().includes(q)
                      );
                    })
                    .map(staff => (
                      <div
                        key={staff.id}
                        className={`staff-option ${selectedStaff?.id === staff.id ? 'selected' : ''}`}
                        onClick={() => setSelectedStaff(staff)}
                      >
                        <div className="staff-avatar">
                          {staff.displayName ? staff.displayName[0].toUpperCase() : '👤'}
                        </div>
                        <div className="staff-info">
                          <h4>{staff.displayName || 'Unknown'}</h4>
                          <p>{staff.email}</p>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="amount">Amount (NGN)</label>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0"
                step="0.01"
                required
                placeholder="Enter transfer amount"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter transfer description"
                rows="3"
              />
            </div>

            <div className="modal-footer">
              <button type="button" className="cancel-button" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="submit-button">
                Transfer Funds
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransferModal;